import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import {BLUE_LIGHT, GRAY, GRAY_LIGHT, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

function Emoji(props: {label: string; symbol: string}): JSX.Element {
  return (
    <span className="emoji" role="img" aria-label={props.label ?? ''} aria-hidden={props.label != null}>
      {props.symbol}
    </span>
  );
}

interface ChecklistItemProps {
  isLast: boolean;
  isOpen: boolean;
  completed: boolean;
  onClick: () => void;
  title: string;
  description?: string | JSX.Element;
  steps?: Array<string>;
}

function ChecklistCircle(props: {completed: boolean; isLast: boolean}): JSX.Element {
  return (
    <>
      {props.completed ? (
        <span className={css(styles.circle, styles.completeCircle)}>
          <BenchlingIcon icon="check" style={{marginBottom: '0px', verticalAlign: '0px'}} />
        </span>
      ) : props.isLast ? (
        <span className={css(styles.circle, styles.lastCircle)}>
          <Emoji label="party-popper" symbol="🎉" />
        </span>
      ) : (
        <span className={css(styles.circle, styles.incompleteCircle)} />
      )}
    </>
  );
}

function AdditionalInformation(props: {
  description?: string | JSX.Element;
  steps?: Array<string>;
}): JSX.Element {
  assert(props.description != null || props.steps != null, 'One of description or steps must be non-null.');
  return (
    <div className={css(styles.bodyText)}>
      {props.description ? (
        <div style={{marginLeft: '42px'}}>{props.description}</div>
      ) : (
        <ol style={{paddingLeft: '52px'}}>
          {props.steps!.map((step, i) => (
            <li className="u-mini-spacing-bottom" key={i}>
              {step}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function ChecklistItem(props: ChecklistItemProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  return (
    <div
      className={classNames(css(styles.container), !props.isOpen && 'u-pointer')}
      style={props.isOpen ? {backgroundColor: '#F5F5F6'} : undefined}
      onClick={props.isOpen ? () => {} : props.onClick}
      {...(props.isLast ? {} : hoverProps)}
    >
      <ChecklistCircle completed={props.completed} isLast={props.isLast} />
      <span className={classNames(css(styles.titleText), props.completed && 'u-faded u-strike-through')}>
        {props.title}
      </span>
      {isHovered && !props.isOpen && (
        <BenchlingIcon
          icon="chevron-right"
          className="pull-right"
          style={{fontSize: '16px', marginTop: '3px'}}
        />
      )}
      {props.isOpen && (
        <>
          <BenchlingIcon
            icon="chevron-down"
            className="pull-right u-pointer"
            style={{fontSize: '16px', marginBottom: '0px'}}
            onClick={props.onClick}
          />
          <AdditionalInformation description={props.description} steps={props.steps} />
        </>
      )}
    </div>
  );
}
const styles = StyleSheet.create({
  container: {
    paddingTop: '13px',
    paddingBottom: '13px',
    paddingRight: '11px',
    ':hover': {
      backgroundColor: '#F5F5F6',
    },
    color: NEAR_BLACK,
  },
  circle: {
    display: 'inline-block',
    height: '20px',
    width: '20px',
    borderRadius: '100px',
    marginLeft: '11px',
    verticalAlign: 'middle',
    position: 'relative',
  },
  completeCircle: {
    padding: '4px',
    backgroundColor: BLUE_LIGHT,
    color: WHITE,
  },
  incompleteCircle: {
    border: `2px solid ${GRAY}`,
    backgroundColor: WHITE,
  },
  lastCircle: {
    paddingLeft: '4px',
    paddingTop: '3px',
    fontSize: '10px',
    backgroundColor: `${GRAY_LIGHT}`,
  },
  titleText: {
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: '14px',
    lineHeight: '16px',
    paddingLeft: '10px',
    verticalAlign: 'middle',
  },
  bodyText: {
    paddingTop: '12px',
    fontSize: '12px',
    lineHeight: '14px',
  },
});
